
import { CACHE_CONFIG, CacheEntry } from './cacheConfig';
import { getUserCacheKeys } from './cacheKeys';
import { isCacheExpired } from './cacheValidation';

/**
 * Clear all cached permissions for a specific user
 */
export const clearUserCache = (userId: string): void => {
  if (!userId) {
    console.warn('clearUserCache: No userId provided');
    return;
  }

  try {
    const userCacheKeys = getUserCacheKeys(userId);
    
    userCacheKeys.forEach(key => {
      localStorage.removeItem(key);
    });
    
    console.log(`Cleared ${userCacheKeys.length} cache entries for user ${userId}`);
  } catch (error) {
    console.error('Error clearing user cache:', error);
  }
};

/**
 * Clear all permissions cache (all users, all versions)
 */
export const clearAllPermissionsCache = (): void => {
  try {
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_CONFIG.PREFIX)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
    
    console.log(`Cleared ${keysToRemove.length} permissions cache entries`);
  } catch (error) {
    console.error('Error clearing all permissions cache:', error);
  }
};

/**
 * Clear expired cache entries to free up storage space
 */
export const clearExpiredCache = (): void => {
  try {
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_CONFIG.PREFIX)) {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const entry: CacheEntry = JSON.parse(cached);
            if (isCacheExpired(entry)) {
              keysToRemove.push(key);
            }
          }
        } catch (parseError) {
          // If we can't parse the entry, it's corrupted - remove it
          keysToRemove.push(key);
        }
      }
    }
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
    
    console.log(`Cleared ${keysToRemove.length} expired/corrupted cache entries`);
  } catch (error) {
    console.error('Error clearing expired cache:', error);
  }
};

/**
 * Invalidate cache on version update
 */
export const invalidateCacheVersion = (oldVersion: string): void => {
  try {
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_CONFIG.PREFIX) && key.endsWith(`_${oldVersion}`)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
    
    console.log(`Invalidated ${keysToRemove.length} cache entries for version ${oldVersion}`);
  } catch (error) {
    console.error('Error invalidating cache version:', error);
  }
};
